import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Wand2, Image as ImageIcon, Loader2, RotateCcw, Save, CheckCircle2, Rocket, Calendar, Link2, ExternalLink, AlertTriangle, X, ChevronDown } from "lucide-react";
import {
  adminContentCategories, adminContentIdeas, adminContentGenerate, adminContentTranslate,
  adminContentImage, adminBlogFullCreate, resolveMediaUrl,
} from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";

// Native <select> + <button> tabs are used everywhere below (no Radix portals) to
// keep the studio robust against browser extensions and auto-translate that would
// otherwise cause React `insertBefore` reconciliation crashes.

const LANGS = [
  { code: "tr", label: "TR (Primary)" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground border-border",
  review: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  approved: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  published: "bg-primary/15 text-primary border-primary/40",
};

const wordCount = (body = []) =>
  body.reduce((n, sec) => n + (sec?.p || []).reduce((m, p) => m + String(p).split(/\s+/).filter(Boolean).length, 0), 0);

export default function ContentStudio({ onSaved }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [genModel, setGenModel] = useState("gpt-5.4");
  const [notes, setNotes] = useState("");
  const [marketBased, setMarketBased] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStage, setGenStage] = useState("");   // "" | "turkish" | "translating"
  const [article, setArticle] = useState(null);   // GeneratedArticle
  const [previewLang, setPreviewLang] = useState("tr");
  const [image, setImage] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [status, setStatus] = useState("draft");
  const [scheduledAt, setScheduledAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedPost, setSavedPost] = useState(null);
  // Persistent banner for budget / auth / rate-limit failures (won't fade like toasts).
  const [apiAlert, setApiAlert] = useState(null); // { kind: "budget" | "rate" | "auth" | "error", message: string }

  const alertFromError = (e, fallback) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || fallback;
    let kind = "error";
    if (status === 402) kind = "budget";
    else if (status === 429) kind = "rate";
    else if (status === 503) kind = "auth";
    return { kind, message: detail };
  };

  useEffect(() => {
    adminContentCategories()
      .then((d) => { setCategories(d.categories || []); if ((d.categories || [])[0]) setCategory(d.categories[0]); })
      .catch(() => toast.error("Failed to load categories"));
  }, []);

  const wc = useMemo(() => (article ? wordCount(article.body?.tr) : 0), [article]);

  const genIdeas = async (marketFlag) => {
    setLoadingIdeas(true); setIdeas([]); setApiAlert(null);
    try {
      const d = await adminContentIdeas({ category, notes, market_based: !!marketFlag });
      setIdeas(d.ideas || []);
      setMarketBased(!!marketFlag);
      if (!(d.ideas || []).length) toast.error("No ideas returned");
    } catch (e) {
      const a = alertFromError(e, "Idea generation failed");
      setApiAlert(a);
      toast.error(a.message, { duration: 10000 });
    } finally { setLoadingIdeas(false); }
  };

  const genArticle = async (idea) => {
    setGenerating(true); setArticle(null); setImage(""); setSavedPost(null); setApiAlert(null);
    let draft = null;
    try {
      // Stage 1: Turkish master article (fast, one LLM call).
      setGenStage("turkish");
      draft = await adminContentGenerate({
        topic: idea.title,
        angle: idea.angle,
        category: idea.category || category,
        market_based: marketBased,
        model: genModel,
      });
      setArticle(draft);
      setPreviewLang("tr");
      setImagePrompt(`${idea.title} — French Riviera, Nice, luxury property lifestyle`);
      toast.success("Turkish draft ready — localizing EN & FR…");
    } catch (e) {
      const a = alertFromError(e, "Article generation failed");
      setApiAlert(a);
      toast.error(a.message, { duration: 10000 });
      setGenerating(false); setGenStage("");
      return;
    }

    // Stage 2: EN + FR translations. If this fails (e.g. budget exceeded mid-flow),
    // keep the Turkish draft visible so the user does not lose their work.
    try {
      setGenStage("translating");
      const translated = await adminContentTranslate({
        title: draft.title,
        description: draft.description,
        excerpt: draft.excerpt,
        body: draft.body,
        faq: draft.faq,
        seo: draft.seo,
        supporting: draft.supporting,
        target_langs: ["en", "fr"],
      });
      setArticle((prev) => ({
        ...(prev || draft),
        title: translated.title,
        description: translated.description,
        excerpt: translated.excerpt,
        body: translated.body,
        faq: translated.faq,
        seo: translated.seo,
        supporting: translated.supporting,
        slug: translated.slug,
      }));
      toast.success("Localized to English & French");
    } catch (e) {
      const a = alertFromError(e, "Localization failed — Turkish draft kept");
      setApiAlert(a);
      toast.error(a.message, { duration: 10000 });
    } finally {
      setGenerating(false); setGenStage("");
    }
  };

  const genImage = async () => {
    if (!imagePrompt.trim()) { toast.error("Add an image prompt first"); return; }
    setGeneratingImage(true); setApiAlert(null);
    try {
      const d = await adminContentImage({ prompt: imagePrompt, style: "editorial-photo" });
      setImage(d.url);
      toast.success("Image generated");
    } catch (e) {
      const a = alertFromError(e, "Image generation failed");
      setApiAlert(a);
      toast.error(a.message, { duration: 10000 });
    } finally { setGeneratingImage(false); }
  };

  const savePost = async (finalStatus, scheduled) => {
    if (!article) return;
    setSaving(true);
    try {
      const payload = {
        slug: article.slug,
        title: article.title,
        description: article.description,
        excerpt: article.excerpt,
        body: article.body,
        faq: article.faq,
        image,
        author: "Velmira Living",
        category: article.category,
        status: finalStatus,
        scheduled_at: scheduled || null,
        seo: article.seo,
        supporting: article.supporting,
        internal_links: article.internal_links,
      };
      const saved = await adminBlogFullCreate(payload);
      setSavedPost(saved);
      setStatus(finalStatus);
      toast.success(finalStatus === "published" ? "Published" : `Saved as ${finalStatus}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = () => {
    setIdeas([]); setArticle(null); setImage(""); setSavedPost(null); setScheduledAt(""); setStatus("draft"); setApiAlert(null);
  };

  return (
    <div data-testid="content-studio" translate="no" className="notranslate space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-border/60 pb-6">
        <div>
          <div className="flex items-center gap-2 text-primary">
            <Sparkles size={16} />
            <span className="font-accent text-[11px] uppercase tracking-[0.35em]">AI Content Studio</span>
          </div>
          <h2 className="mt-1 font-headings text-2xl text-foreground">Draft a multilingual article in minutes</h2>
          <p className="mt-1 font-body text-sm text-foreground/60">Turkish is generated first, then localized to English & French. SEO metadata, social captions, image and internal links included.</p>
        </div>
        {(ideas.length || article) ? (
          <button onClick={reset} data-testid="studio-reset" className="inline-flex items-center gap-2 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 text-xs uppercase tracking-widest transition-all">
            <RotateCcw size={13} /> Start over
          </button>
        ) : null}
      </div>

      {/* Persistent API alert (budget exhausted, rate limit, auth) — will not fade like toasts. */}
      {apiAlert ? (
        <div
          data-testid={`studio-api-alert-${apiAlert.kind}`}
          className={`border p-4 flex items-start gap-3 ${
            apiAlert.kind === "budget"
              ? "border-yellow-500/50 bg-yellow-500/5 text-foreground/90"
              : "border-red-500/40 bg-red-500/5 text-foreground/90"
          }`}
        >
          <AlertTriangle size={18} className={apiAlert.kind === "budget" ? "text-yellow-400 mt-0.5" : "text-red-400 mt-0.5"} />
          <div className="min-w-0 flex-1">
            <p className="font-body text-sm leading-relaxed">{apiAlert.message}</p>
            {apiAlert.kind === "budget" ? (
              <p className="mt-2 font-body text-xs text-foreground/60">
                In Emergent: Profile → Universal Key → <b>Add Balance</b> (or turn on Auto Top-up). Then click a &ldquo;Write article&rdquo; or &ldquo;Generate&rdquo; button again.
              </p>
            ) : null}
          </div>
          <button
            onClick={() => setApiAlert(null)}
            data-testid="studio-api-alert-dismiss"
            className="p-1 text-foreground/60 hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {/* 1. Idea generation */}
      <section className="border border-border/60 bg-secondary p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-body">1</span>
          <h3 className="font-headings text-lg text-foreground">Propose article ideas</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="studio-category" className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">Category</label>
            <div className="relative">
              <select
                id="studio-category"
                data-testid="studio-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={!categories.length}
                className="w-full h-10 appearance-none bg-background border border-border/60 px-3 pr-9 font-body text-sm text-foreground focus:outline-none focus:border-primary transition-colors disabled:opacity-60"
              >
                {!categories.length && <option value="">Loading categories…</option>}
                {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            </div>
          </div>
          <div>
            <label className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">Optional guidance</label>
            <Input
              value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="studio-notes"
              placeholder="e.g. Focus on families relocating for the school year"
              className="bg-background border-border/60"
            />
          </div>
          <div>
            <label htmlFor="studio-model" className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">AI model (article generation)</label>
            <div className="relative">
              <select
                id="studio-model"
                data-testid="studio-model"
                value={genModel}
                onChange={(e) => setGenModel(e.target.value)}
                className="w-full h-10 appearance-none bg-background border border-border/60 px-3 pr-9 font-body text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
              >
                <option value="gpt-5.4">GPT-5.4 (OpenAI — recommended)</option>
                <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (Anthropic)</option>
                <option value="claude-sonnet-5">Claude Sonnet 5 (Anthropic — newest)</option>
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={() => genIdeas(false)}
            disabled={loadingIdeas || !category}
            data-testid="studio-generate-monthly"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
          >
            {loadingIdeas ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Generate Monthly Article Ideas
          </button>
          <button
            onClick={() => genIdeas(true)}
            disabled={loadingIdeas || !category}
            data-testid="studio-generate-market"
            className="inline-flex items-center gap-2 border border-primary/60 text-primary hover:bg-primary/10 px-5 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
          >
            {loadingIdeas ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            Generate Market-Based Ideas
          </button>
        </div>

        {ideas.length > 0 && (
          <div className="mt-6 grid gap-3" data-testid="studio-ideas">
            {ideas.map((i, idx) => (
              <div key={i.title || idx} data-testid={`studio-idea-${idx}`} className="border border-border/60 bg-background p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wider mb-2">{i.category}</Badge>
                  <p className="font-headings text-base text-foreground leading-snug">{i.title}</p>
                  <p className="mt-1 font-body text-sm text-foreground/70"><span className="text-primary">Angle:</span> {i.angle}</p>
                  <p className="mt-1 font-body text-xs text-foreground/50"><span className="uppercase tracking-widest text-foreground/40">Why now:</span> {i.why_now}</p>
                </div>
                <button
                  onClick={() => genArticle(i)}
                  disabled={generating}
                  data-testid={`studio-write-${idx}`}
                  className="inline-flex items-center gap-2 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
                >
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  Write article
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 2. Article preview — keyed by article identity so translate-time shape changes remount cleanly. */}
      {(generating || article) && (
        <section key={article ? `article-${article.slug || "draft"}` : "generating"} className="border border-border/60 bg-secondary p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-body">2</span>
            <h3 className="font-headings text-lg text-foreground">Review & edit</h3>
            {article && (
              <span className="ml-auto text-xs font-body text-foreground/50">
                {wc} words · <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{article.category}</Badge>
              </span>
            )}
          </div>

          {generating && (
            <div className="flex items-center gap-3 py-8 text-foreground/70 font-body text-sm">
              <Loader2 size={18} className="animate-spin text-primary" />
              {genStage === "turkish" ? "Drafting the Turkish master article (~30–60s)…" :
               genStage === "translating" ? "Localizing to English & French (~30–60s)…" :
               "Working…"}
            </div>
          )}

          {article && (
            <>
              {/* Native button-based lang switcher — no Radix portals */}
              <div data-testid="studio-lang-tabs" className="inline-flex bg-background border border-border/60 p-1 gap-1">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setPreviewLang(l.code)}
                    data-testid={`studio-lang-${l.code}`}
                    data-active={previewLang === l.code ? "true" : "false"}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest font-body transition-colors ${
                      previewLang === l.code
                        ? "bg-primary/20 text-primary"
                        : "text-foreground/60 hover:text-foreground"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              {(() => {
                const l = LANGS.find((x) => x.code === previewLang) || LANGS[0];
                return (
                  <div key={l.code} className="mt-6 space-y-6">
                    <div>
                      <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-1">Title</p>
                      <Input
                        value={article.title[l.code] || ""}
                        onChange={(e) => setArticle({ ...article, title: { ...article.title, [l.code]: e.target.value } })}
                        data-testid={`studio-title-${l.code}`}
                        className="bg-background border-border/60 font-headings text-lg"
                      />
                    </div>
                    <div>
                      <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-1">Excerpt</p>
                      <Textarea
                        value={article.excerpt[l.code] || ""}
                        onChange={(e) => setArticle({ ...article, excerpt: { ...article.excerpt, [l.code]: e.target.value } })}
                        data-testid={`studio-excerpt-${l.code}`}
                        rows={2} className="bg-background border-border/60"
                      />
                    </div>
                    <div>
                      <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">Body</p>
                      <div data-testid={`studio-body-${l.code}`} className="space-y-4 max-h-[420px] overflow-y-auto pr-3 border-l border-primary/30 pl-4">
                        {(article.body[l.code] || []).map((sec, si) => (
                          <div key={sec.h2 || si}>
                            {sec.h2 && <h4 className="font-headings text-base text-foreground mb-1.5">{sec.h2}</h4>}
                            {(sec.p || []).map((p, pi) => (<p key={pi} className="font-body text-sm text-foreground/80 leading-relaxed mb-2">{p}</p>))}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">SEO metadata</p>
                        <div className="space-y-3 text-sm font-body">
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">SEO Title</label>
                            <Input
                              value={article.seo[l.code]?.seo_title || ""}
                              onChange={(e) => setArticle({ ...article, seo: { ...article.seo, [l.code]: { ...(article.seo[l.code] || {}), seo_title: e.target.value } } })}
                              className="bg-background border-border/60 mt-1"
                              data-testid={`studio-seo-title-${l.code}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">Meta Description</label>
                            <Textarea
                              value={article.seo[l.code]?.meta_description || ""}
                              onChange={(e) => setArticle({ ...article, seo: { ...article.seo, [l.code]: { ...(article.seo[l.code] || {}), meta_description: e.target.value } } })}
                              rows={2} className="bg-background border-border/60 mt-1"
                              data-testid={`studio-seo-meta-${l.code}`}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-foreground/50 uppercase tracking-widest">OG Title</label>
                              <Input
                                value={article.seo[l.code]?.og_title || ""}
                                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, [l.code]: { ...(article.seo[l.code] || {}), og_title: e.target.value } } })}
                                className="bg-background border-border/60 mt-1"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-foreground/50 uppercase tracking-widest">OG Description</label>
                              <Input
                                value={article.seo[l.code]?.og_description || ""}
                                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, [l.code]: { ...(article.seo[l.code] || {}), og_description: e.target.value } } })}
                                className="bg-background border-border/60 mt-1"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">Slug</label>
                            <Input
                              value={article.slug || ""}
                              onChange={(e) => setArticle({ ...article, slug: e.target.value })}
                              className="bg-background border-border/60 mt-1 font-mono text-xs"
                              data-testid="studio-slug"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">Supporting content</p>
                        <div className="space-y-3 text-sm font-body">
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">LinkedIn post</label>
                            <Textarea
                              value={article.supporting[l.code]?.linkedin_post || ""}
                              onChange={(e) => setArticle({ ...article, supporting: { ...article.supporting, [l.code]: { ...(article.supporting[l.code] || {}), linkedin_post: e.target.value } } })}
                              rows={4} className="bg-background border-border/60 mt-1"
                              data-testid={`studio-linkedin-${l.code}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">Instagram caption</label>
                            <Textarea
                              value={article.supporting[l.code]?.instagram_caption || ""}
                              onChange={(e) => setArticle({ ...article, supporting: { ...article.supporting, [l.code]: { ...(article.supporting[l.code] || {}), instagram_caption: e.target.value } } })}
                              rows={3} className="bg-background border-border/60 mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">Newsletter excerpt</label>
                            <Textarea
                              value={article.supporting[l.code]?.newsletter_excerpt || ""}
                              onChange={(e) => setArticle({ ...article, supporting: { ...article.supporting, [l.code]: { ...(article.supporting[l.code] || {}), newsletter_excerpt: e.target.value } } })}
                              rows={3} className="bg-background border-border/60 mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-foreground/50 uppercase tracking-widest">Pull quote</label>
                            <Input
                              value={article.supporting[l.code]?.featured_quote || ""}
                              onChange={(e) => setArticle({ ...article, supporting: { ...article.supporting, [l.code]: { ...(article.supporting[l.code] || {}), featured_quote: e.target.value } } })}
                              className="bg-background border-border/60 mt-1 italic"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Internal linking suggestions */}
              <div className="mt-8 border-t border-border/60 pt-6">
                <div className="flex items-center gap-2 mb-3">
                  <Link2 size={15} className="text-primary" />
                  <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary">Suggested internal links</p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="studio-internal-links">
                  {(article.internal_links || []).map((l, i) => (
                    <a key={l.slug || i} href={`/en/${l.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-body border border-border/60 hover:border-primary hover:text-primary px-3 py-1.5 transition-all">
                      {l.label} <ExternalLink size={11} />
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {/* 3. Featured image */}
      {article && (
        <section className="border border-border/60 bg-secondary p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-body">3</span>
            <h3 className="font-headings text-lg text-foreground">Featured image</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">Image prompt</label>
              <Textarea
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                rows={4}
                data-testid="studio-image-prompt"
                className="bg-background border-border/60"
              />
              <div className="mt-3 flex gap-3">
                <button
                  onClick={genImage}
                  disabled={generatingImage}
                  data-testid="studio-generate-image"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
                >
                  {generatingImage ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                  Generate with Nano Banana
                </button>
              </div>
              <div className="mt-4">
                <label className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">Or paste image URL</label>
                <Input
                  value={image} onChange={(e) => setImage(e.target.value)}
                  placeholder="https://…"
                  data-testid="studio-image-url"
                  className="bg-background border-border/60"
                />
              </div>
            </div>
            <div className="flex items-center justify-center bg-background border border-border/60 min-h-[260px]" data-testid="studio-image-preview">
              {image ? (
                <img src={resolveMediaUrl(image)} alt="preview" className="max-h-[320px] w-full object-cover" />
              ) : (
                <p className="text-foreground/40 font-body text-sm">Preview will appear here.</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 4. Publish */}
      {article && (
        <section className="border border-border/60 bg-secondary p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-body">4</span>
            <h3 className="font-headings text-lg text-foreground">Publish workflow</h3>
            {savedPost && (
              <Badge className={`ml-2 ${STATUS_STYLES[savedPost.status] || STATUS_STYLES.draft} border`}>
                {savedPost.status}
              </Badge>
            )}
          </div>
          {savedPost ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 font-body text-sm">
              <CheckCircle2 size={18} className="text-primary" />
              <span>Saved as <b>{savedPost.status}</b>{savedPost.scheduled_at ? <> — scheduled for <b>{savedPost.scheduled_at}</b></> : null}. Slug: <span className="font-mono text-xs text-primary">/{savedPost.slug}</span></span>
              <a href={`/en/blog/${savedPost.slug}`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1.5 border border-border/60 hover:border-primary hover:text-primary px-3 py-1.5 text-xs uppercase tracking-widest transition-all">
                Preview <ExternalLink size={12} />
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="studio-status" className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">Approval status</label>
                  <div className="relative">
                    <select
                      id="studio-status"
                      data-testid="studio-status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full h-10 appearance-none bg-background border border-border/60 px-3 pr-9 font-body text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                    >
                      <option value="draft">Draft</option>
                      <option value="review">Review</option>
                      <option value="approved">Approved</option>
                      <option value="published">Published</option>
                    </select>
                    <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50" />
                  </div>
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-foreground/60 mb-1.5 block">Schedule (optional)</label>
                  <Input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    data-testid="studio-schedule"
                    className="bg-background border-border/60"
                  />
                  <p className="mt-1 text-[11px] text-foreground/50">If set, the post stays hidden on the public site until this date & time (UTC).</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  onClick={() => savePost("draft", null)}
                  disabled={saving}
                  data-testid="studio-save-draft"
                  className="inline-flex items-center gap-2 border border-border/60 hover:border-primary hover:text-primary px-5 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save draft
                </button>
                <button
                  onClick={() => savePost(status, scheduledAt ? new Date(scheduledAt).toISOString() : null)}
                  disabled={saving}
                  data-testid="studio-save-status"
                  className="inline-flex items-center gap-2 bg-primary/20 text-primary hover:bg-primary/30 px-5 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />}
                  Save as {status}
                </button>
                <button
                  onClick={() => savePost("published", null)}
                  disabled={saving}
                  data-testid="studio-publish"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
                  Publish now
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
